/**
 * @fileoverview Column visibility state for the bank transactions tables.
 *
 * Split out of `ColumnsMenu.tsx` (react-refresh/only-export-components) —
 * `useHiddenColumns` is the persistence hook the "All Transactions" and
 * "Documents → Accounts" tables use alongside `<ColumnsMenu>`.
 */

import { useCallback, useState } from "react";

/**
 * Session-persistent set of hidden column keys.
 *
 * @param storageKey - Unique `localStorage` key for this table.
 * @returns `[hidden, toggle]` — the set of hidden keys and a toggle callback.
 */
export function useHiddenColumns(
  storageKey: string,
): readonly [Set<string>, (key: string) => void] {
  const [hidden, setHidden] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? new Set(JSON.parse(raw) as string[]) : new Set<string>();
    } catch {
      return new Set<string>();
    }
  });

  const toggle = useCallback(
    (key: string) => {
      setHidden((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        try {
          localStorage.setItem(storageKey, JSON.stringify([...next]));
        } catch {
          // Ignore storage failures (private mode / quota) — state still works
          // for the current session.
        }
        return next;
      });
    },
    [storageKey],
  );

  return [hidden, toggle] as const;
}
